"use client"

import { useEffect, useRef, useState } from "react"
import Win95Button from "../Win95Button"
import ChatAuthForm from "./ChatAuthForm"
import ChatSettingsMenu from "./ChatSettingsMenu"

export type ChatMessage = {
	id: string
	username: string
	text: string
	timestamp: string
}

type ChatWindowProps = {
	username: string | null
	messages: ChatMessage[]
	onSend: (text: string) => Promise<string | null>
	onAuthed: (username: string) => void
	onLogout: () => void
}

const NAME_COLORS = ["#ff2d78", "#39ff14", "#00f5ff", "#ffe600", "#bf00ff", "#ff6f00"]

function colorFor(username: string): string {
	let hash = 0
	for (let i = 0; i < username.length; i++) hash = (hash * 31 + username.charCodeAt(i)) >>> 0
	return NAME_COLORS[hash % NAME_COLORS.length]
}

function formatTimestamp(iso: string): string {
	const d = new Date(iso)
	const day = String(d.getDate()).padStart(2, "0")
	const month = String(d.getMonth() + 1).padStart(2, "0")
	const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
	return `${day}/${month}/${d.getFullYear()} ${time}`
}

export default function ChatWindow({ username, messages, onSend, onAuthed, onLogout }: ChatWindowProps) {
	const [draft, setDraft] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [sending, setSending] = useState(false)
	const [showSettings, setShowSettings] = useState(false)
	const scrollRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const el = scrollRef.current
		if (el) el.scrollTop = el.scrollHeight
	}, [messages.length])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const text = draft.trim()
		if (!text || sending) return
		setSending(true)
		setError(null)
		const failure = await onSend(text)
		if (failure) {
			setError(failure)
		} else {
			setDraft("")
		}
		setSending(false)
	}

	return (
		<div
			className="
				flex-1 min-h-0
				bg-[#0a0015]
				border-2 border-t-[#39ff14] border-l-[#39ff14] border-b-[#bf00ff] border-r-[#bf00ff]
				flex flex-col
			"
		>
			<div className="relative flex items-center justify-between bg-gradient-to-r from-[#bf00ff] to-[#ff2d78] text-black font-bold text-sm px-2 py-1">
				<span>#trashovision-chat</span>
				{username && (
					<div className="flex items-center gap-2">
						<span className="hidden sm:inline">logged in as {username}</span>
						<button
							onClick={() => setShowSettings((v) => !v)}
							aria-label="Settings"
							className="text-sm leading-none hover:opacity-70"
						>
							⚙️
						</button>
						<button
							onClick={onLogout}
							className="text-xs underline underline-offset-2 hover:opacity-70"
						>
							log out
						</button>
					</div>
				)}
				{showSettings && <ChatSettingsMenu onClose={() => setShowSettings(false)} />}
			</div>

			<div
				ref={scrollRef}
				className="flex-1 min-h-0 overflow-y-auto p-2 font-mono text-sm space-y-1"
			>
				{messages.length === 0 ? (
					<div className="italic text-[#7755aa]">No messages yet. Say hi!</div>
				) : (
					messages.map((m) => (
						<div key={m.id} className="text-[#e0d0ff] break-words">
							<span className="text-[#7755aa]">[{formatTimestamp(m.timestamp)}]</span>{" "}
							<span className="font-bold" style={{ color: colorFor(m.username) }}>
								&lt;{m.username}&gt;
							</span>{" "}
							{m.text}
						</div>
					))
				)}
			</div>

			<div className="border-t-2 border-[#1a0033] p-2">
				{username ? (
					<>
						{error && <div className="pb-1 text-xs text-[#ff003c] font-bold">{error}</div>}
						<form onSubmit={handleSubmit} className="flex gap-1">
							<input
								value={draft}
								onChange={(e) => setDraft(e.target.value)}
								maxLength={500}
								placeholder="say something..."
								className="flex-1 min-w-0 bg-black text-[#39ff14] border-2 border-t-[#ff003c] border-l-[#ff003c] border-b-[#00f5ff] border-r-[#00f5ff] px-2 py-1 text-sm outline-none font-mono"
							/>
							<Win95Button label={sending ? "..." : "Send"} fitWidth />
						</form>
					</>
				) : (
					<ChatAuthForm onAuthed={onAuthed} />
				)}
			</div>
		</div>
	)
}
